import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';

const FORM_URL = 'https://forms.gle/3aGFXDkKs1pNn4wU7';

// Nested plans: [tutor][type]
const plans = {
  hamza: {
    label: 'A-Levels with Hamza',
    private: {
      type: 'private',
      label: 'Private Tuition',
      price: '£140',
      period: '/month',
      hourly: 'Based on £35/hr',
      hourlyValue: 35,
      features: [
        '1 private (1:1) session per week',
        'Priority WhatsApp support',
        'Custom revision plan',
        'Mock exam marking',
        'Past paper packs',
      ],
      cta: 'Choose Private Tuition',
    },
    group: {
      type: 'group',
      label: 'Group Tuition',
      price: '£60',
      period: '/month',
      hourly: 'Based on £15/hr',
      hourlyValue: 15,
      features: [
        '1 group lesson per week (small groups of 8)',
        'Email support',
        'Monthly progress check',
      ],
      cta: 'Choose Group Tuition',
    },
  },
  fatima: {
    label: 'GCSE with Fatima',
    private: {
      type: 'private',
      label: 'Private Tuition',
      price: '£100',
      period: '/month',
      hourly: 'Based on £25/hr',
      hourlyValue: 25,
      features: [
        '1 private (1:1) session per week',
        'Priority WhatsApp support',
        'Custom revision plan',
        'Mock exam marking',
        'Past paper packs',
      ],
      cta: 'Choose Private Tuition',
    },
    group: {
      type: 'group',
      label: 'Group Tuition',
      price: '£60',
      period: '/month',
      hourly: 'Based on £15/hr',
      hourlyValue: 15,
      features: [
        '1 group lesson per week (small groups of 8)',
        'Email support',
        'Monthly progress check',
      ],
      cta: 'Choose Group Tuition',
    },
  },
};

const TogglePill = ({ label, isOn, onClick }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className={`px-5 py-2 rounded-full text-[12px] md:text-[13px] font-medium border transition-colors duration-300 ${
      isOn
        ? 'bg-neutral-900 text-white border-neutral-900'
        : 'bg-white text-neutral-900 border-neutral-300 hover:border-neutral-500'
    }`}
  >
    {label}
  </motion.button>
);

const Pricing = () => {
  const [tutor, setTutor] = useState(null); // 'hamza' | 'fatima' | null
  const [tuitionType, setTuitionType] = useState(null); // 'private' | 'group' | null

  const selectTutor = (t) => {
    setTutor(t);
    setTuitionType(null); // reset the inner toggle when tutor changes
  };

  const plan = tutor && tuitionType ? plans[tutor][tuitionType] : null;

  return (
    <section id="plans" className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="font-display text-[40px] md:text-[56px] tracking-tight font-semibold text-neutral-900"
          >
            Choose your plan.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-4 text-[15px] text-neutral-600"
          >
            Choose what suits you — cancel any time.
          </motion.p>
        </div>

        {/* Tutor toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 justify-center"
        >
          <TogglePill
            label="A-Levels with Hamza"
            isOn={tutor === 'hamza'}
            onClick={() => selectTutor('hamza')}
          />
          <TogglePill
            label="GCSE with Fatima"
            isOn={tutor === 'fatima'}
            onClick={() => selectTutor('fatima')}
          />
        </motion.div>

        {/* Tuition type toggle — appears after selecting a tutor */}
        <AnimatePresence>
          {tutor && (
            <motion.div
              key="type-toggle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 flex flex-col sm:flex-row gap-3 justify-center"
            >
              <TogglePill
                label="Private Tuition"
                isOn={tuitionType === 'private'}
                onClick={() => setTuitionType('private')}
              />
              <TogglePill
                label="Group Tuition"
                isOn={tuitionType === 'group'}
                onClick={() => setTuitionType('group')}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Plan card */}
        <AnimatePresence mode="wait">
          {plan && (
            <motion.div
              key={`${tutor}-${plan.type}`}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12 md:mt-16 grid md:grid-cols-2 gap-6 md:gap-10 items-stretch"
            >
              {/* Plan card (left) */}
              <div className="rounded-3xl bg-white text-neutral-900 p-8 md:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.06)] border border-neutral-200">
                <div className="font-display text-[18px] md:text-[20px] font-semibold text-neutral-900">
                  {plan.label}
                </div>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-[64px] md:text-[80px] font-semibold leading-none tracking-tight text-neutral-900">
                    {plan.price}
                  </span>
                  <span className="text-[15px] text-neutral-500">{plan.period}</span>
                </div>

                <p className="mt-2 text-[14px] md:text-[15px] text-neutral-600">{plan.hourly}</p>

                <ul className="mt-7 space-y-3">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2.5 text-[14px] md:text-[15px] leading-[1.55]"
                    >
                      <Check size={16} className="mt-0.5 shrink-0 text-neutral-900" />
                      <span className="text-neutral-700">{f}</span>
                    </li>
                  ))}
                </ul>

                <motion.a
                  href={FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="pill-btn mt-9 w-full inline-flex items-center justify-center bg-neutral-900 text-white py-3.5 text-[14px] font-medium hover:bg-neutral-700"
                >
                  {plan.cta}
                </motion.a>
              </div>

              {/* Right column — descriptive paragraph */}
              <div className="flex items-center">
                {plan.type === 'group' && (
                  <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="text-[15px] md:text-[16px] leading-[1.75] text-neutral-700"
                  >
                    Learning with others doesn&apos;t mean getting lost in the crowd. These
                    sessions are kept small and highly focused — with up to 8 students max —
                    so the environment stays personal and interactive.
                    <br />
                    <br />
                    You will collaborate with peers, tackle tough exam-style questions
                    together, and still get the direct, individual feedback you need to build
                    your confidence each week.
                  </motion.p>
                )}
                {plan.type === 'private' && (
                  <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="text-[15px] md:text-[16px] leading-[1.75] text-neutral-700"
                  >
                    If you need dedicated focus on specific blind spots, 1:1 sessions give you
                    undivided attention to master them completely. At £{plan.hourlyValue} per
                    hour (billed monthly), every lesson is customised entirely around your
                    goals, your exam board, and the exact techniques you need to perfect.
                    <br />
                    <br />
                    Our standard schedule starts at 1 hour per week, but we can easily adjust
                    the frequency to match your academic goals.
                  </motion.p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Pricing;
